/**
 * Copyright (c) 2026 Spicy Friends Ltd.
 * All rights reserved.
 *
 * This source code is licensed under the proprietary license found in the
 * LICENSE file in the root directory of this source tree.
 */
import { env } from '@web/src/lib/env.client'

// A first install downloads the whole precache before activating, which can take a while on slow networks.
const SERVICE_WORKER_TIMEOUT_MS = 30_000
export const SERVICE_WORKER_SCRIPT = '/service-worker.js'
export const SERVICE_WORKER_SCOPE = '/'

const REGISTRATION_OPTIONS: RegistrationOptions = {
  scope: SERVICE_WORKER_SCOPE,
  updateViaCache: 'none',
}

export const isServiceWorkerEnabled = (): boolean =>
  env.VITE_ENABLE_PWA && typeof navigator !== 'undefined' && 'serviceWorker' in navigator

/**
 * With the PWA disabled the worker script is gone, and a 404 on the update check keeps the old
 * worker installed. Remove it and its caches so it stops serving stale assets.
 */
export async function removeServiceWorkers(): Promise<void> {
  if (env.VITE_ENABLE_PWA || typeof navigator === 'undefined' || !('serviceWorker' in navigator)) {
    return
  }

  const registrations = await navigator.serviceWorker.getRegistrations()
  if (registrations.length === 0) {
    return
  }

  await Promise.all(registrations.map((registration) => registration.unregister()))

  if ('caches' in window) {
    const cacheNames = await caches.keys()
    await Promise.all(cacheNames.map((cacheName) => caches.delete(cacheName)))
  }
}

let serviceWorkerRegistrationPromise: Promise<ServiceWorkerRegistration> | null = null

// `navigator.serviceWorker.ready` never resolves when no worker is registered, and
// iPadOS can leave it pending in an installed PWA. Register the script and wait
// until that registration is active instead.
export function ensureServiceWorkerRegistration(): Promise<ServiceWorkerRegistration> {
  if (!isServiceWorkerEnabled()) {
    return Promise.reject(new Error('Service workers are disabled'))
  }

  if (!serviceWorkerRegistrationPromise) {
    serviceWorkerRegistrationPromise = activateServiceWorker().catch((error: unknown) => {
      serviceWorkerRegistrationPromise = null
      throw error
    })
  }

  return serviceWorkerRegistrationPromise
}

async function activateServiceWorker(): Promise<ServiceWorkerRegistration> {
  let registration = await navigator.serviceWorker.getRegistration(SERVICE_WORKER_SCOPE)

  if (registration && !registration.active && !registration.installing && !registration.waiting) {
    await registration.unregister()
    registration = undefined
  }

  if (!registration) {
    registration = await navigator.serviceWorker.register(SERVICE_WORKER_SCRIPT, REGISTRATION_OPTIONS)
  }

  if (!registration.active) {
    await waitForServiceWorkerActivated(registration)
  }

  if (!registration.active) {
    throw new Error('Push notifications require an active service worker')
  }

  return registration
}

// A worker that goes redundant may have been replaced by a newer install, so follow
// the registration's newest worker instead of failing on the first one.
function waitForServiceWorkerActivated(registration: ServiceWorkerRegistration): Promise<void> {
  if (registration.active) {
    return Promise.resolve()
  }

  let cleanUp = () => {
    /* noop */
  }

  const activated = new Promise<void>((resolve, reject) => {
    let trackedWorker: ServiceWorker | null = null

    const onStateChange = () => {
      if (registration.active) {
        cleanUp()
        resolve()
        return
      }

      if (trackedWorker?.state !== 'redundant') return

      const nextWorker = registration.installing ?? registration.waiting
      if (nextWorker && nextWorker !== trackedWorker) {
        track(nextWorker)
        return
      }

      cleanUp()
      reject(new Error('The service worker failed to activate'))
    }

    const track = (worker: ServiceWorker) => {
      trackedWorker?.removeEventListener('statechange', onStateChange)
      trackedWorker = worker
      worker.addEventListener('statechange', onStateChange)
    }

    const onUpdateFound = () => {
      if (registration.installing) track(registration.installing)
    }

    cleanUp = () => {
      trackedWorker?.removeEventListener('statechange', onStateChange)
      registration.removeEventListener('updatefound', onUpdateFound)
    }

    const initialWorker = registration.installing ?? registration.waiting
    if (!initialWorker) {
      reject(new Error('Push notifications require an active service worker'))
      return
    }

    registration.addEventListener('updatefound', onUpdateFound)
    track(initialWorker)
    onStateChange()
  })

  return withTimeout(activated, SERVICE_WORKER_TIMEOUT_MS, 'Timed out waiting for the service worker').finally(() =>
    cleanUp()
  )
}

export function withTimeout<T>(promise: Promise<T>, timeoutMs: number, message: string): Promise<T> {
  return new Promise((resolve, reject) => {
    const timeoutId = window.setTimeout(() => {
      reject(new Error(message))
    }, timeoutMs)

    promise.then(
      (value) => {
        window.clearTimeout(timeoutId)
        resolve(value)
      },
      (error: unknown) => {
        window.clearTimeout(timeoutId)
        reject(error)
      }
    )
  })
}
